import { useEffect, useRef, useState } from 'react';
import {
  macroTargets,
  type ActivityLevel,
  type GoalType,
  type ProfileInput,
  type Sex,
} from '@calorie/shared';
import { haptic } from '../lib/telegram.js';

/** Развёрнутые формулировки - для системного выбора, где места хватает. */
const ACTIVITY_OPTIONS: Record<ActivityLevel, string> = {
  sedentary: 'Сидячий образ жизни',
  light: 'Лёгкая, 1–2 тренировки',
  moderate: 'Умеренная, 3–4 тренировки',
  high: 'Высокая, 5–6 тренировок',
  athlete: 'Спортсмен, тяжёлый труд',
};

/** Короткие - для строки списка, чтобы значение не переносилось. */
const ACTIVITY_SHORT: Record<ActivityLevel, string> = {
  sedentary: 'Сидячий',
  light: 'Лёгкая',
  moderate: 'Умеренная',
  high: 'Высокая',
  athlete: 'Спортсмен',
};

const GOAL_LABELS: Record<GoalType, string> = {
  lose: 'Похудеть',
  maintain: 'Держать',
  gain: 'Набрать',
};

/** Пауза после последнего нажатия клавиши, через которую уходит сохранение. */
const AUTOSAVE_DELAY_MS = 800;

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

interface Props {
  initial?: ProfileInput | null;
  onSave: (input: ProfileInput) => Promise<void>;
  /** Задан, когда экран открыт как настройки, а не как первичная анкета. */
  onBack?: () => void;
}

function Chevron({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 5l7 7-7 7" />
    </svg>
  );
}

/** Сегментированный переключатель в духе iOS: белая «таблетка» по сиреневой дорожке. */
function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  const index = options.findIndex((o) => o.value === value);
  return (
    <div className="seg-ios" style={{ '--count': options.length } as React.CSSProperties}>
      <span
        className="seg-ios-pill"
        style={{ transform: `translateX(${index * 100}%)` }}
        aria-hidden="true"
      />
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className="seg-ios-item"
          aria-pressed={o.value === value}
          onClick={() => {
            haptic();
            onChange(o.value);
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Анкета: по ней считается суточная норма калорий и БЖУ.
 *
 * В настройках изменения уходят на сервер сами, с паузой после последнего
 * нажатия, - кнопки сохранения нет. При первом заполнении автосохранения нет:
 * пока человек не подтвердил анкету, у него ещё нет нормы, и промежуточные
 * значения записывать нечего.
 */
export function Onboarding({ initial, onSave, onBack }: Props) {
  const isSettings = Boolean(onBack);

  const [sex, setSex] = useState<Sex>(initial?.sex ?? 'male');
  const [age, setAge] = useState(String(initial?.ageYears ?? 30));
  const [height, setHeight] = useState(String(initial?.heightCm ?? 175));
  const [weight, setWeight] = useState(String(initial?.weightKg ?? 70));
  const [activity, setActivity] = useState<ActivityLevel>(
    initial?.activityLevel ?? 'light',
  );
  const [goal, setGoal] = useState<GoalType>(initial?.goalType ?? 'maintain');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');

  const draft: ProfileInput = {
    sex,
    ageYears: Number(age) || 0,
    heightCm: Number(height) || 0,
    weightKg: Number(weight) || 0,
    activityLevel: activity,
    goalType: goal,
  };

  const valid =
    draft.ageYears >= 10 &&
    draft.ageYears <= 120 &&
    draft.heightCm >= 80 &&
    draft.weightKg >= 25;

  // Норма пересчитывается прямо во время заполнения - видно, на что влияет ответ
  const preview = valid ? macroTargets(draft) : null;

  const values = JSON.stringify(draft);
  // Сравниваем с исходными значениями, а не считаем проходы эффекта:
  // в dev StrictMode монтирует дважды, и счётчик пропускал бы правку либо,
  // наоборот, слал пустое сохранение при простом открытии настроек.
  const initialValues = useRef(values);

  useEffect(() => {
    if (!isSettings) return;
    if (values === initialValues.current) return;
    // Недописанное число («9» вместо «91») не должно уехать на сервер
    if (!valid) return;

    const timer = setTimeout(async () => {
      setSaveState('saving');
      try {
        await onSave(JSON.parse(values) as ProfileInput);
        setSaveState('saved');
      } catch {
        setSaveState('error');
      }
    }, AUTOSAVE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [values, valid, isSettings, onSave]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await onSave(draft);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить');
      setBusy(false);
    }
  }

  return (
    <div className="app settings">
      {isSettings ? (
        <header className="nav">
          <button className="nav-back" onClick={onBack}>
            <Chevron className="nav-back-icon" />
            Дневник
          </button>
          <span className={`nav-state ${saveState}`} role="status">
            {saveState === 'saving' && 'Сохраняем…'}
            {saveState === 'saved' && 'Сохранено'}
            {saveState === 'error' && 'Не сохранилось'}
          </span>
        </header>
      ) : (
        <header className="nav" />
      )}

      <h1 className="large-title">
        {isSettings ? 'Настройки' : 'О себе'}
      </h1>

      <p className="group-caption">Данные о себе</p>
      <div className="group">
        <div className="cell">
          <span className="cell-label">Пол</span>
          <Segmented
            value={sex}
            onChange={setSex}
            options={[
              { value: 'male' as Sex, label: 'Мужской' },
              { value: 'female' as Sex, label: 'Женский' },
            ]}
          />
        </div>

        <div className="cell">
          <label className="cell-label" htmlFor="age">Возраст</label>
          <span className="cell-value">
            <input id="age" className="cell-input" type="number" inputMode="numeric"
                   value={age} onChange={(e) => setAge(e.target.value)} />
            <span className="cell-unit">лет</span>
          </span>
        </div>

        <div className="cell">
          <label className="cell-label" htmlFor="height">Рост</label>
          <span className="cell-value">
            <input id="height" className="cell-input" type="number" inputMode="numeric"
                   value={height} onChange={(e) => setHeight(e.target.value)} />
            <span className="cell-unit">см</span>
          </span>
        </div>

        <div className="cell">
          <label className="cell-label" htmlFor="weight">Вес</label>
          <span className="cell-value">
            <input id="weight" className="cell-input" type="number" inputMode="decimal"
                   value={weight} onChange={(e) => setWeight(e.target.value)} />
            <span className="cell-unit">кг</span>
          </span>
        </div>
      </div>
      {!valid && (
        <p className="group-footer warning">
          Проверьте возраст, рост и вес - пока не сохраняю.
        </p>
      )}

      <p className="group-caption">Образ жизни</p>
      <div className="group">
        {/* Нативный select лежит поверх строки прозрачным слоем: так на телефоне
            открывается системный выбор, а строка выглядит как в настройках iOS */}
        <div className="cell cell-tappable">
          <span className="cell-label">Активность</span>
          <span className="cell-value cell-value-muted">
            {ACTIVITY_SHORT[activity]}
            <Chevron className="cell-chevron" />
          </span>
          <select
            className="cell-select"
            value={activity}
            aria-label="Активность"
            onChange={(e) => setActivity(e.target.value as ActivityLevel)}
          >
            {Object.entries(ACTIVITY_OPTIONS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        <div className="cell">
          <span className="cell-label">Цель</span>
          <Segmented
            value={goal}
            onChange={setGoal}
            options={(Object.keys(GOAL_LABELS) as GoalType[]).map((g) => ({
              value: g,
              label: GOAL_LABELS[g],
            }))}
          />
        </div>
      </div>

      {preview && (
        <>
          <p className="group-caption">Ваша норма</p>
          <div className="group">
            <div className="cell">
              <span className="cell-label">Калории</span>
              <span className="cell-value cell-value-strong">{preview.kcal} ккал</span>
            </div>
            <div className="cell">
              <span className="cell-label">Белки</span>
              <span className="cell-value cell-value-muted">{preview.proteinG} г</span>
            </div>
            <div className="cell">
              <span className="cell-label">Жиры</span>
              <span className="cell-value cell-value-muted">{preview.fatG} г</span>
            </div>
            <div className="cell">
              <span className="cell-label">Углеводы</span>
              <span className="cell-value cell-value-muted">{preview.carbsG} г</span>
            </div>
          </div>
        </>
      )}

      <p className="group-footer">
        Норма считается по формуле Миффлина - Сан Жеора.
        {isSettings ? ' Изменения сохраняются автоматически.' : ''}
      </p>

      {error && <p className="group-footer warning">{error}</p>}

      {!isSettings && (
        <button className="btn" onClick={submit} disabled={!valid || busy}>
          {busy ? 'Сохраняем…' : 'Продолжить'}
        </button>
      )}
    </div>
  );
}
